import { memo, useEffect, useState } from "react";
import { LuClock, LuUsers, LuX } from "react-icons/lu";
import { getRecipe } from "../../../network/service/ShopService";
import ProductImage from "./ProductImage";

export const RecipeCard = memo(function RecipeCard({ recipe, onOpen }) {
  return (
    <button type="button" className="ep-recipe" onClick={() => onOpen(recipe.id)} aria-label={`${recipe.name}, ${recipe.cookMinutes} minutes`}>
      <ProductImage path={recipe.imagePath} alt="" />
      <div>
        <b>{recipe.name}</b>
        <span>
          <LuClock aria-hidden="true" /> {recipe.cookMinutes} mins
        </span>
      </div>
    </button>
  );
});

/** Horizontal row (or grid) of recipe cards; opening one shows the full recipe. */
export function RecipeList({ recipes, grid = false }) {
  const [openId, setOpenId] = useState(null);
  return (
    <>
      <div className={`ep-recipes ${grid ? "is-grid" : ""}`}>
        {recipes.map((recipe) => (
          <RecipeCard key={recipe.id} recipe={recipe} onOpen={setOpenId} />
        ))}
      </div>
      {openId && <RecipeModal id={openId} onClose={() => setOpenId(null)} />}
    </>
  );
}

function RecipeModal({ id, onClose }) {
  const [state, setState] = useState({ recipe: null, error: "" });

  useEffect(() => {
    let alive = true;
    getRecipe(id)
      .then((recipe) => alive && setState({ recipe, error: "" }))
      .catch((error) => alive && setState({ recipe: null, error: error.message }));
    const onKey = (e) => e.key === "Escape" && onClose();
    window.addEventListener("keydown", onKey);
    return () => {
      alive = false;
      window.removeEventListener("keydown", onKey);
    };
  }, [id, onClose]);

  const { recipe, error } = state;
  return (
    <div className="ep-modal-backdrop" onClick={onClose}>
      <div className="ep-modal" role="dialog" aria-modal="true" aria-label={recipe?.name ?? "Recipe"} onClick={(e) => e.stopPropagation()}>
        <div className="ep-modal-head">
          <h2>{recipe?.name ?? "Recipe"}</h2>
          <button type="button" className="ep-icon-btn" aria-label="Close recipe" onClick={onClose}>
            <LuX />
          </button>
        </div>
        <div className="ep-modal-body">
          {error && <p className="ep-prose">{error}</p>}
          {!recipe && !error && <div className="ep-skel" style={{ height: 180 }} />}
          {recipe && (
            <>
              {recipe.imagePath && <ProductImage path={recipe.imagePath} alt="" style={{ maxHeight: 240, objectFit: "cover" }} />}
              {recipe.imageCredit && (
                <p style={{ fontSize: 11, color: "var(--ep-faint)", margin: "4px 0 0" }}>{recipe.imageCredit}</p>
              )}
              <div style={{ height: 12 }} />
              <div className="ep-row" style={{ color: "var(--ep-muted)", fontSize: 13, marginBottom: 10 }}>
                <span>
                  <LuClock aria-hidden="true" /> {recipe.cookMinutes} mins
                </span>
                {recipe.servings && (
                  <span>
                    <LuUsers aria-hidden="true" /> Serves {recipe.servings}
                  </span>
                )}
              </div>
              {recipe.description && <p className="ep-prose" style={{ marginBottom: 12 }}>{recipe.description}</p>}
              {recipe.ingredients.length > 0 && (
                <>
                  <h3 className="ep-section-title" style={{ margin: "8px 0" }}>Ingredients</h3>
                  <ul>
                    {recipe.ingredients.map((item, i) => (
                      <li key={i}>{item}</li>
                    ))}
                  </ul>
                </>
              )}
              {recipe.steps.length > 0 && (
                <>
                  <h3 className="ep-section-title" style={{ margin: "12px 0 8px" }}>Method</h3>
                  <ol>
                    {recipe.steps.map((step, i) => (
                      <li key={i}>{step}</li>
                    ))}
                  </ol>
                </>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}
